import ChipInput from "@/components/base/ChipInput";
import { useTranslation } from "@/hooks/useTranslation";
import { useCreateOrder } from "@/services";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	Button,
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	Icon,
	Input,
	Label,
	RadioGroup,
	RadioGroupItem,
} from "@st-marketplace/ui";
import { useId, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import BuyerSelect from "./BuyerSelect";
import { addOrderAccountSchema } from "./schema";

export const sellOrderSchema = addOrderAccountSchema.and(
	z.object({
		relation_id: z.string().min(1),
		amount: z.coerce
			.number()
			.int()
			.positive()
			.max(Math.floor(Number.MAX_SAFE_INTEGER / 1000)),
		reference: z.string(),
	}),
);
type SellOrderData = z.infer<typeof sellOrderSchema>;

export default function SellOrderDialog({
	open,
	onClose,
}: { open: boolean; onClose: () => void }) {
	const { t } = useTranslation();
	const createOrder = useCreateOrder();
	const id = useId();
	const [passwordVisible, setPasswordVisible] = useState(false);
	const form = useForm<SellOrderData>({
		resolver: zodResolver(sellOrderSchema),
		defaultValues: {
			relation_id: "",
			amount: 0,
			reference: "",
			email: "",
			password: "",
			authType: "backup_code",
			gauth: "",
			backup_code: [],
		},
	});
	const pending = createOrder.isPending;
	const close = () => {
		if (!pending) {
			form.reset();
			setPasswordVisible(false);
			onClose();
		}
	};
	const submit = (data: SellOrderData) => {
		if (pending) return;
		createOrder.mutate(
			{
				body: {
					relation_id: data.relation_id,
					amount: data.amount * 1000,
					platform: "ps",
					external_id: data.reference.trim() || undefined,
					ea_email: data.email,
					ea_password: data.password,
					auth:
						data.authType === "gauth"
							? { type: "gauth", auth: data.gauth.trim() }
							: { type: "backup_code", auth: data.backup_code },
				},
			},
			{
				onSuccess: () => {
					form.reset();
					setPasswordVisible(false);
					onClose();
				},
			},
		);
	};
	return (
		<Dialog
			open={open}
			onOpenChange={(next) => {
				if (!next) close();
			}}
		>
			<DialogContent
				showCloseButton={false}
				aria-describedby={undefined}
				className="flex min-h-0 w-[calc(100vw-32px)] max-w-[640px] flex-col gap-0 overflow-hidden p-0"
				style={{ maxHeight: "calc(100dvh - 32px)" }}
			>
				<DialogHeader className="shrink-0 flex-row items-center justify-between">
					<DialogTitle>{t("order.sellCoin")}</DialogTitle>
					<Button
						type="button"
						icon
						variant="text"
						color="ghost"
						size="sm"
						aria-label={t("action.cancel")}
						onClick={close}
						disabled={pending}
					>
						<Icon name="close-line" size="sm" />
					</Button>
				</DialogHeader>
				<Form {...form}>
					<form
						onSubmit={form.handleSubmit(submit)}
						noValidate
						autoComplete="off"
						className="flex min-h-0 flex-col"
					>
						<div className="min-h-0 overflow-y-auto">
							<fieldset disabled={pending} className="min-w-0 space-y-6 p-6">
								<FormField
									control={form.control}
									name="relation_id"
									render={({ field }) => (
										<FormItem>
											<FormLabel required id={`${id}-buyer-label`}>
												{t("addOrder.chooseBuyer")}
											</FormLabel>
											<BuyerSelect
												id={`${id}-buyer`}
												value={field.value}
												onChange={field.onChange}
												disabled={pending}
											/>
											<FormMessage />
										</FormItem>
									)}
								/>
								<div className="grid gap-5 sm:grid-cols-2">
									<FormField
										control={form.control}
										name="amount"
										render={({ field }) => (
											<FormItem>
												<FormLabel required>
													{t("addOrder.inputAmount")}
												</FormLabel>
												<FormControl>
													<Input
														{...field}
														value={field.value || ""}
														type="number"
														min="1"
														step="1"
														inputMode="numeric"
														suffix={
															<span>{t("signs.thousands").toUpperCase()}</span>
														}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
									<FormField
										control={form.control}
										name="reference"
										render={({ field }) => (
											<FormItem>
												<FormLabel>{t("addOrder.reference")}</FormLabel>
												<FormControl>
													<Input {...field} />
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>
							</fieldset>
							<fieldset
								disabled={pending}
								className="min-w-0 space-y-5 border-t border-secondary-border p-6"
							>
								<div className="grid gap-5 sm:grid-cols-2">
									<FormField
										control={form.control}
										name="email"
										render={({ field }) => (
											<FormItem>
												<FormLabel required>
													{t("addOrder.inputEmail")}
												</FormLabel>
												<FormControl>
													<Input
														{...field}
														name={`ea-account-${id}`}
														ltr
														type="text"
														inputMode="email"
														autoComplete="off"
														autoCapitalize="none"
														autoCorrect="off"
														spellCheck={false}
														data-1p-ignore
														data-lpignore="true"
														placeholder="name@example.com"
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
									<FormField
										control={form.control}
										name="password"
										render={({ field }) => (
											<FormItem>
												<FormLabel required>
													{t("addOrder.inputPassword")}
												</FormLabel>
												<FormControl>
													<Input
														{...field}
														name={`ea-password-${id}`}
														ltr
														type={passwordVisible ? "text" : "password"}
														autoComplete="new-password"
														data-1p-ignore
														data-lpignore="true"
														append={
															<Button
																type="button"
																icon
																variant="text"
																color="ghost"
																size="sm"
																aria-label={
																	passwordVisible
																		? "Hide password"
																		: "Show password"
																}
																onClick={() =>
																	setPasswordVisible(!passwordVisible)
																}
															>
																<Icon
																	name={
																		passwordVisible
																			? "eye-off-line"
																			: "eye-line"
																	}
																	size="sm"
																/>
															</Button>
														}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>
								<div className="space-y-4">
									<RadioGroup
										aria-label={t("addOrder.authMethod")}
										value={form.watch("authType")}
										onValueChange={(value) =>
											form.setValue(
												"authType",
												value as SellOrderData["authType"],
											)
										}
										className="flex flex-wrap gap-6"
									>
										<div className="flex items-center gap-2">
											<RadioGroupItem value="backup_code" id={`${id}-backup`} />
											<Label htmlFor={`${id}-backup`}>
												{t("addOrder.inputBackupCode")}
											</Label>
										</div>
										<div className="flex items-center gap-2">
											<RadioGroupItem value="gauth" id={`${id}-gauth`} />
											<Label htmlFor={`${id}-gauth`}>
												{t("addOrder.inputGauth")}
											</Label>
										</div>
									</RadioGroup>
									{form.watch("authType") === "gauth" ? (
										<FormField
											control={form.control}
											name="gauth"
											render={({ field }) => (
												<FormItem>
													<FormLabel required>
														{t("addOrder.inputGauth")}
													</FormLabel>
													<FormControl>
														<Input {...field} ltr autoComplete="off" />
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>
									) : (
										<ChipInput
											chips={form.watch("backup_code")}
											onChipsChange={(codes) =>
												form.setValue("backup_code", codes, {
													shouldValidate: true,
												})
											}
											error={form.formState.errors.backup_code?.message}
											disabled={pending}
											label={t("addOrder.inputBackupCode")}
										/>
									)}
								</div>
							</fieldset>
						</div>
						<div className="flex shrink-0 justify-end gap-3 border-t border-secondary-border px-6 py-4">
							<Button
								type="button"
								color="secondary"
								onClick={close}
								disabled={pending}
							>
								{t("action.cancel")}
							</Button>
							<Button
								type="submit"
								color="primary"
								loading={pending}
								disabled={pending}
							>
								{t("addOrder.placeOrder")}
							</Button>
						</div>
					</form>
				</Form>
			</DialogContent>
		</Dialog>
	);
}
